import { EventHandler } from "../../utils/event-handler";
import { ScrollBar } from "../../utils/scrollbar";
import { debounce, throttle } from "../../utils";
import { EVENT_CLICK, EVENT_RESIZE, EVENT_SCROLL } from "../../utils/events";

export class Navbar {
    #SELECTOR_NAVBAR = '.navbar';
    #SELECTOR_TOGGLER = `${this.#SELECTOR_NAVBAR}__toggler[aria-controls]`;
    #SELECTOR_HEADER_NAVBAR_STICKY = `.header--navbar-sticky`;

    #CLASS_NAME_NAVBAR_OPEN = 'open';
    #CLASS_NAME_TOGGLER_ACTIVE = 'active';
    #CLASS_NAME_NAVBAR_HIDDEN = 'hidden';

    #PROPERTY_NAVBAR_MOBILE = '--navbar-mobile';

    static EVENT_NAVBAR_SHOW = 'navbar-show';
    static EVENT_NAVBAR_SHOWN = 'navbar-shown';
    static EVENT_NAVBAR_HIDE = 'navbar-hide';
    static EVENT_NAVBAR_HIDDEN = 'navbar-hidden';

    #navbar: HTMLElement;
    #toggler: HTMLElement;
    #content: HTMLElement;

    get #mobileBehaviour(): boolean {
        return getComputedStyle(this.#navbar).getPropertyValue(this.#PROPERTY_NAVBAR_MOBILE) === 'true';
    };
    #lastScrollY: number;
    #navbarHeight: number;

    constructor() {
        this.#initElements();
        this.#initEvents();
    }

    #initElements(): void {
        this.#navbar = document.querySelector(this.#SELECTOR_NAVBAR);
        this.#toggler = document.querySelector(this.#SELECTOR_TOGGLER);

        if (this.#toggler) {
            this.#content = document.getElementById(this.#toggler.getAttribute('aria-controls'));

            this.#setAriaExpanded(this.#isShown());
        }

        this.#saveNavbarHeight();
    }

    #initEvents(): void {
        if (this.#toggler) {
            EventHandler.on(this.#toggler, EVENT_CLICK, () => {
                this.toggle();
            });
        }

        EventHandler.on(window, EVENT_SCROLL, throttle(() => {
            this.#toggleNavbarPosition();
        }, 100));

        EventHandler.on(window, EVENT_RESIZE, debounce(() => {
            this.#saveNavbarHeight();
        }, 100));
    }

    #isShown(): boolean {
        return this.#content.classList.contains(this.#CLASS_NAME_NAVBAR_OPEN);
    }

    #setAriaExpanded(isOpen: boolean): void {
        this.#toggler.setAttribute('aria-expanded', String(isOpen));
    }

    toggle(): void {
        if (this.#isShown()) {
            this.hide();
        } else {
            this.show();
        }
    }

    show(): void {
        if (this.#isShown()) {
            return;
        }

        ScrollBar.hide();

        EventHandler.trigger(this.#content, Navbar.EVENT_NAVBAR_SHOW);

        this.#toggler.classList.add(this.#CLASS_NAME_TOGGLER_ACTIVE);
        this.#content.classList.add(this.#CLASS_NAME_NAVBAR_OPEN);

        EventHandler.trigger(this.#content, Navbar.EVENT_NAVBAR_SHOWN);

        this.#setAriaExpanded(true);
    }

    hide(): void {
        if (!this.#isShown()) {
            return;
        }

        ScrollBar.reset();

        EventHandler.trigger(this.#content, Navbar.EVENT_NAVBAR_HIDE);

        this.#toggler.classList.remove(this.#CLASS_NAME_TOGGLER_ACTIVE);
        this.#content.classList.remove(this.#CLASS_NAME_NAVBAR_OPEN);

        EventHandler.trigger(this.#content, Navbar.EVENT_NAVBAR_HIDDEN);

        this.#setAriaExpanded(false);
    }

    #saveNavbarHeight(): void {
        this.#navbarHeight = this.#navbar.offsetHeight;
    }

    #toggleNavbarPosition(): void {
        if (!this.#mobileBehaviour) {
            this.#navbar.classList.remove(this.#CLASS_NAME_NAVBAR_HIDDEN);

            return;
        }

        if (this.#navbar.closest(this.#SELECTOR_HEADER_NAVBAR_STICKY) !== null) {
            return;
        }

        const scrollY = window.scrollY;

        if (scrollY > this.#lastScrollY && scrollY > this.#navbarHeight) {
            this.#navbar.classList.add(this.#CLASS_NAME_NAVBAR_HIDDEN);
        } else if (scrollY < this.#lastScrollY) {
            this.#navbar.classList.remove(this.#CLASS_NAME_NAVBAR_HIDDEN);
        }

        this.#lastScrollY = scrollY;
    }
}
